import React from "react";

import { DialogActions } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  BillingDocumentAction,
  closeConfirmModal,
  openConfirmModal,
} from "actions";

import { LedgerEntriesTable } from "components/BillingWorkFlow/LedgerEntriesTable";
import { PDFPreview } from "components/BillingWorkFlow/PDFPreview";
import { MultiButton } from "components/Button";
import { Button, SecondaryButton } from "components/Form";
import { Column, Row } from "components/Layout";
import { Dialog, DialogContent, DialogTitle } from "components/MaterialDialog";
import { AbsolutePaper } from "components/Paper";

import {
  BillingDocumentReportJobStatus,
  BillingWarningsWhiteList,
  InactiveIntegrationStatuses,
} from "constants/billingDocuments";
import { BillingLedgerEntryStatus } from "constants/billingLedgerEntries";
import { IntegrationType } from "constants/integrations";

import {
  billingDocumentAllowsReversal,
  expectingDeleteWithActiveIntegrationOnAcceptChanges,
} from "lib/billingDocument";
import { getBillingDocumentReportJobUrl } from "lib/navigation";

import {
  getActiveRole,
  getBillingDocumentById,
  getIntegrationBusinessesByTypeByBusinessId,
  getPaymentIdsByBillingDocumentId,
  selectBusinessForBillingDocumentByIdLookup,
  selectLedgerEntriesByDocumentId,
  selectWarningsByBillingDocumentIdLookup,
} from "selectors";

import { useHasXeroIntegrationPermission } from "hooks";

const Heading = styled.h1(
  ({ theme }) => `
  flex: 1;
  text-align: center;
  border-bottom: 1px solid ${theme.colors.controlBorder};
  margin: 0;
`,
);

const viewChoices = {
  BOTH: 0,
  PDF_ONLY: 1,
  LEDGER_ENTRIES_ONLY: 2,
};

export const ResolveChanges = ({ onClose, id }) => {
  const dispatch = useDispatch();

  const billingDocument = useSelector(getBillingDocumentById(id)) || {};
  const paymentIds = useSelector(getPaymentIdsByBillingDocumentId(id)) || [];
  const ledgerEntriesByDocumentId = useSelector(
    selectLedgerEntriesByDocumentId,
  );

  const businessForBillingDocumentIdLookup = useSelector(
    selectBusinessForBillingDocumentByIdLookup,
  );

  const business = businessForBillingDocumentIdLookup[id] || {};

  const xeroIntegrationBusinesses = useSelector(
    getIntegrationBusinessesByTypeByBusinessId(
      IntegrationType.Xero,
      business.id,
    ),
  );

  const businessHasXeroIntegrationBusiness = Boolean(
    xeroIntegrationBusinesses.length,
  );

  const warningsByBillingDocumentIdLookup = useSelector(
    selectWarningsByBillingDocumentIdLookup,
  );

  const billingDocumentWarnings = warningsByBillingDocumentIdLookup[id];

  const disabledAcceptWithoutReversalReasons = [];
  const disabledAcceptAndExportReasons = [];

  const documentIsVoidOrDeleted = InactiveIntegrationStatuses.includes(
    billingDocument.integrationStatus,
  );

  if (paymentIds.length > 0) {
    disabledAcceptWithoutReversalReasons.push(
      "This document has a payment assigned to it in Xero, to accept changes without a reversal, undo the payment.",
    );
  }

  // TODO Confirm if this statement is true!
  // if (billingDocument.proceedsRetainedByBillingDocument) {
  //   disabledAcceptWithoutReversalReasons.push(
  //     "This document has retained proceeds, to accept changes without a reversal, first remove all retained proceeds.",
  //   );
  // }

  const disabledAcceptWithReversalReasons = [];
  if (!billingDocumentAllowsReversal(billingDocument)) {
    disabledAcceptWithReversalReasons.push(
      "Reversals are only possible on documents in a PAID or AUTHORISED state in Xero.",
    );
  }
  if (billingDocument.proceedsRetainedByBillingDocument) {
    disabledAcceptWithReversalReasons.push(
      "Reversals are only possible on documents without Retained Proceeds, you must remove all retained proceeds first.",
    );
  }

  if (
    expectingDeleteWithActiveIntegrationOnAcceptChanges(
      billingDocument,
      ledgerEntriesByDocumentId,
    )
  ) {
    const expectingDeleteMessage =
      "This document has been exported to Xero, however this change will remove this document. DELETE or VOID in Xero before accepting this change.";
    disabledAcceptWithoutReversalReasons.push(expectingDeleteMessage);
    disabledAcceptWithReversalReasons.push(expectingDeleteMessage);
  }

  if (billingDocumentWarnings.length) {
    billingDocumentWarnings
      .filter(warning => !BillingWarningsWhiteList.includes(warning))
      .forEach(warning => disabledAcceptAndExportReasons.push(warning));
  }

  if (!businessHasXeroIntegrationBusiness) {
    disabledAcceptAndExportReasons.push("Business has not been mapped in Xero");
  }

  const allowWithoutReversal =
    disabledAcceptWithoutReversalReasons.length === 0;

  const userRoleSlug = useSelector(state => getActiveRole(state).slug);

  const hasXeroPermission = useHasXeroIntegrationPermission();

  const showReversal = hasXeroPermission;
  const showAcceptAndExport = hasXeroPermission && allowWithoutReversal;

  const allowReversal = disabledAcceptWithReversalReasons.length === 0;
  const allowAcceptAndExport = disabledAcceptAndExportReasons.length === 0;

  const acceptChanges = () => {
    dispatch(BillingDocumentAction.acceptChanges({ id }));
    onClose();
  };

  const acceptChangesAndExport = () => {
    dispatch(BillingDocumentAction.acceptChangesAndExport({ id }));
    onClose();
  };

  const acceptAndReverse = () => {
    dispatch(BillingDocumentAction.acceptAndReverse({ id }));
    onClose();
  };

  const confirmReversal = () => {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: "Are you sure you want to reverse this document?",
        actions: [
          {
            label: "No",
            secondary: true,
            onClick: () => {
              dispatch(closeConfirmModal());
            },
          },
          {
            label: "Yes",
            onClick: () => {
              dispatch(closeConfirmModal());
              acceptAndReverse();
            },
          },
        ],
      }),
    );
  };

  const rejectChanges = () => {
    dispatch(BillingDocumentAction.rejectChanges({ id }));
    onClose();
  };

  const getDocumentPreviewByStatus = status =>
    getBillingDocumentReportJobUrl(
      id,
      userRoleSlug,
      status,
      billingDocument.lastModified,
    );

  const [viewIndex, setViewIndex] = React.useState(0);
  const changeView = () => {
    const next =
      viewIndex + 1 === Object.keys(viewChoices).length ? 0 : viewIndex + 1;
    setViewIndex(next);
  };

  const view = Object.values(viewChoices).indexOf(viewIndex);

  const buttons = [
    {
      title: "Accept Changes",
      isDisabled: false,
      tooltip: allowWithoutReversal
        ? ""
        : disabledAcceptWithoutReversalReasons.join("\n"),
      onClick: acceptChanges,
    },
    showAcceptAndExport && {
      title: "Accept and Export",
      isDisabled: !allowAcceptAndExport,
      tooltip: allowAcceptAndExport
        ? ""
        : disabledAcceptAndExportReasons.join("\n"),
      onClick: acceptChangesAndExport,
      default: documentIsVoidOrDeleted && allowAcceptAndExport,
    },
    showReversal && {
      title: "Accept with Reversal",
      isDisabled: !allowReversal,
      tooltip: allowReversal
        ? "Reversals for sent or exported documents will clearly show revisions in a statement section within this document and will automatically resolve changes via the Xero integration."
        : disabledAcceptWithReversalReasons.join("\n"),
      onClick: confirmReversal,
    },
  ].filter(Boolean);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen>
      <DialogTitle onClose={onClose}>
        <Row justifyBetween alignCenter>
          <span>Review Changes</span>
          <div>
            <Button onClick={changeView}>Toggle View</Button>
          </div>
        </Row>
      </DialogTitle>
      <DialogContent dividers>
        <Column fullHeight>
          <Row>
            <Heading>Current</Heading>
            <Heading>Changes</Heading>
          </Row>
          {view !== viewChoices.LEDGER_ENTRIES_ONLY && (
            <Row full>
              <Row relative full className="border-r-1 border-b-1">
                <PDFPreview
                  url={getDocumentPreviewByStatus(
                    BillingDocumentReportJobStatus.COMMITTED,
                  )}
                />
              </Row>
              <Row relative full className="border-b-1">
                <PDFPreview
                  url={getDocumentPreviewByStatus(
                    BillingDocumentReportJobStatus.TEMPORARY,
                  )}
                />
              </Row>
            </Row>
          )}
          {view !== viewChoices.PDF_ONLY && (
            <Row full>
              <Row relative full className="border-r-1">
                <AbsolutePaper>
                  <LedgerEntriesTable
                    billingDocumentId={id}
                    status={BillingLedgerEntryStatus.COMMITTED}
                  />
                </AbsolutePaper>
              </Row>
              <Row relative full>
                <AbsolutePaper>
                  <LedgerEntriesTable
                    billingDocumentId={id}
                    status={BillingLedgerEntryStatus.TEMPORARY}
                  />
                </AbsolutePaper>
              </Row>
            </Row>
          )}
        </Column>
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>

        <SecondaryButton onClick={rejectChanges}>
          Reject Changes
        </SecondaryButton>

        <MultiButton buttons={buttons} flex />
      </DialogActions>
    </Dialog>
  );
};
